import React from "react";
import { 
  FileText, 
  Edit3, 
  File, 
  MessageSquare, 
  Globe, 
  Calendar,
  ArrowRight,
  Gavel,
  Users,
  Scale,
  CheckCircle,
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color = "bg-gradient-to-r from-gray-700 to-gray-500", change, changeType }) => (
  <div className={`${color} text-white rounded-3xl p-5 shadow-[0_10px_25px_rgba(0,0,0,0.08)] backdrop-blur-xl`}>
    <div className="flex items-center justify-between mb-3">
      <div className="text-sm opacity-90">{title}</div>
      {Icon && <Icon size={20} className="opacity-90" />}
    </div>
    <div className="flex items-center justify-between">
      <div className="text-4xl font-bold">{value}</div>
      {change && (
        <div className={`text-sm ${changeType === 'positive' ? 'text-green-200' : 'text-red-200'}`}>
          {change}
        </div>
      )}
    </div>
  </div>
);

const MeetingItem = ({ time, title, type, client, urgent = false }) => (
  <div className={`flex items-center justify-between py-3 px-4 rounded-lg ${urgent ? 'bg-red-50 border-l-4 border-red-500' : 'bg-gray-50'}`}>
    <div className="flex items-center gap-3">
      <div className={`w-3 h-3 rounded-full ${urgent ? 'bg-red-500' : 'bg-blue-500'}`} />
      <div>
        <div className="text-sm font-medium text-gray-800">{title}</div>
        <div className="text-xs text-gray-500">{client} • {type}</div>
      </div>
    </div>
    <div className="text-sm text-gray-600">{time}</div>
  </div>
);

const TaskItem = ({ title, priority, dueDate, completed = false }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-3">
      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${completed ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
        {completed && <CheckCircle size={12} className="text-white" />}
      </div>
      <div>
        <div className={`text-sm ${completed ? 'line-through text-gray-500' : 'text-gray-800'}`}>{title}</div>
        <div className="text-xs text-gray-500">Due: {dueDate}</div>
      </div>
    </div>
    <span className={`text-xs px-2 py-1 rounded-full ${
      priority === 'High' ? 'bg-red-100 text-red-600' : 
      priority === 'Medium' ? 'bg-yellow-100 text-yellow-600' : 
      'bg-green-100 text-green-600'
    }`}>
      {priority}
    </span>
  </div>
);

const FeatureCard = ({ title, description, icon: Icon, color, onClick }) => (
  <div 
    className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
    onClick={onClick}
  >
    <div className="flex items-center gap-3 mb-2">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <h3 className="font-semibold text-gray-800">{title}</h3>
    </div>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

const LawFirmDashboard = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">Law Firm Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage your firm's cases, clients, and team collaboration</p>
        </div>
      </div>

      {/* Law Firm Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Active Cases" 
          value="47" 
          icon={Scale} 
          color="bg-[#0d3b66]" 
          change="+3 this week" 
          changeType="positive" 
        />
        <StatCard 
          title="Total Clients" 
          value="128" 
          icon={Users} 
          color="bg-[#059669]" 
          change="+5 this month" 
          changeType="positive" 
        />
        <StatCard 
          title="Upcoming Hearings" 
          value="12" 
          icon={Gavel} 
          color="bg-[#dc2626]" 
          change="3 this week" 
          changeType="neutral" 
        />
        <StatCard 
          title="Documents Processed" 
          value="234" 
          icon={FileText} 
          color="bg-[#7c3aed]" 
          change="+18 today" 
          changeType="positive" 
        />
      </div>

      {/* Middle: Upcoming Meetings + Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Upcoming Meetings & Hearings */}
        <div className="lg:col-span-4 bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold flex items-center gap-2">
              <Calendar size={20} className="text-[#0d3b66]" />
              Today's Schedule
            </div>
            <button className="text-sm text-[#0d3b66] hover:underline">View All</button>
          </div>
          <div className="space-y-2">
            <MeetingItem 
              time="9:00 AM" 
              title="Client Consultation" 
              type="Meeting" 
              client="Johnson & Associates" 
              urgent={true}
            />
            <MeetingItem 
              time="11:30 AM" 
              title="Court Hearing" 
              type="Criminal Case" 
              client="State vs. Smith" 
            />
            <MeetingItem 
              time="2:00 PM" 
              title="Document Review" 
              type="Contract" 
              client="ABC Corp" 
            />
            <MeetingItem 
              time="4:30 PM" 
              title="Team Meeting" 
              type="Internal" 
              client="Law Firm Staff" 
            />
          </div>
        </div>

        {/* Tasks & Case Management */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold flex items-center gap-2">
              <CheckCircle size={20} className="text-[#059669]" />
              Today's Tasks
            </div>
            <button className="text-sm text-[#0d3b66] hover:underline">View All</button>
          </div>
          <div className="space-y-3">
            <TaskItem 
              title="Prepare motion for summary judgment" 
              priority="High" 
              dueDate="Today" 
            />
            <TaskItem 
              title="Review contract terms with client" 
              priority="Medium" 
              dueDate="Tomorrow" 
              completed={true}
            />
            <TaskItem 
              title="File discovery documents" 
              priority="High" 
              dueDate="Today" 
            />
            <TaskItem 
              title="Update case notes" 
              priority="Low" 
              dueDate="This week" 
            />
            <TaskItem 
              title="Schedule deposition" 
              priority="Medium" 
              dueDate="Tomorrow" 
            />
          </div>
        </div>

        {/* Team Members */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold flex items-center gap-2">
              <Users size={20} className="text-[#0d3b66]" />
              Team Members
            </div>
            <button className="text-sm text-[#0d3b66] hover:underline">Manage</button>
          </div>
          <div className="space-y-3">
            {[
              { name: "Sarah Johnson", role: "Senior Partner", status: "Online" },
              { name: "Michael Chen", role: "Associate", status: "Online" },
              { name: "Emily Davis", role: "Junior Associate", status: "Away" },
              { name: "David Wilson", role: "Paralegal", status: "Online" },
            ].map((member, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">{member.name}</div>
                    <div className="text-xs text-gray-500">{member.role}</div>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  member.status === 'Online' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                }`}>
                  {member.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* App Features */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Firm Tools & Features</h2>
          <button className="text-sm text-[#0d3b66] hover:underline flex items-center gap-1">
            View All <ArrowRight size={16} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard
            title="Document Translation"
            description="Translate legal documents between multiple languages with AI accuracy"
            icon={Globe}
            color="bg-[#0d3b66]"
            onClick={() => window.location.href = '/dashboard/translate'}
          />
          <FeatureCard
            title="Draft Management"
            description="Create, edit, and manage legal document drafts efficiently"
            icon={Edit3}
            color="bg-[#059669]"
            onClick={() => window.location.href = '/dashboard/drafts'}
          />
          <FeatureCard
            title="Doc AI Assistant"
            description="AI-powered legal document analysis and insights"
            icon={File}
            color="bg-[#7c3aed]"
            onClick={() => window.location.href = '/dashboard/docai'}
          />
          <FeatureCard
            title="Query Bot"
            description="Get instant answers to legal questions and case research"
            icon={MessageSquare}
            color="bg-[#dc2626]"
            onClick={() => window.location.href = '/dashboard/query'}
          />
          <FeatureCard
            title="Available Documents"
            description="Access and manage your document library and templates"
            icon={FileText}
            color="bg-[#ea580c]"
            onClick={() => window.location.href = '/dashboard/available-documents'}
          />
          <FeatureCard
            title="Advocate Diary"
            description="Track case progress, deadlines, and important events"
            icon={Calendar}
            color="bg-[#0891b2]"
            onClick={() => window.location.href = '/dashboard/adv-diary'}
          />
        </div>
      </div>
    </div>
  );
};

export default LawFirmDashboard;
